import { z } from "zod";
import {
  normalizeApiBaseUrl,
  parseErrorResponse,
  SharedApiError,
  type FetchLike,
} from "./shared";
import {
  autoJoinResponseSchema,
  communityPreviewSchema,
  publicChannelContentSchema,
  publicChannelsResponseSchema,
  publicContributorsResponseSchema,
  publicQuestionResponseSchema,
  publicWorkspaceMetadataSchema,
  requestJoinResponseSchema,
  userReputationPath,
  userReputationSchema,
  userProfilePath,
  userProfileSchema,
  type UserProfile,
  type AutoJoinResponse,
  type CommunityPreview,
  type PublicChannelContent,
  type PublicChannelsResponse,
  type PublicContributorsResponse,
  type PublicQuestionResponse,
  type PublicWorkspaceMetadata,
  type RequestJoinBody,
  type RequestJoinResponse,
  type UserReputation,
} from "./public-workspaces-contracts";

// Wire contracts (Zod schemas + inferred types + `userReputationPath`) live in
// `./public-workspaces-contracts` and are re-exported here so the public import
// surface (`@ano/shared/api/public-workspaces`) stays unchanged.
export * from "./public-workspaces-contracts";

/**
 * Public workspace surface — the endpoints a non-member (or logged-out
 * visitor) calls when landing on `<slug>.ano.dev` for the first time.
 *
 * Unauthenticated reads (each returns `null` on 404 — never leak the
 * existence of a private workspace):
 *   - `GET .../public`       metadata (see `fetchPublicWorkspaceMetadata`)
 *   - `GET .../preview`      Phase 1 redacted digest (`fetchCommunityPreview`)
 *   - `GET .../channels`     Phase 5 public-channel index (`fetchPublicChannels`)
 *   - `GET .../c/:channel`   Phase 5 per-channel content (`fetchPublicChannelContent`)
 *   - `GET .../c/:channel/m/:id` single shared question + answers (`fetchPublicQuestion`)
 *   - `GET .../contributors` Phase 3 top contributors (`fetchPublicContributors`)
 *   - `GET /api/users/:id/reputation` portable reputation (`fetchUserReputation`)
 *
 * Authenticated join writes (must succeed — no 404⇒null path):
 *
 *   POST /api/workspaces/by-slug/:slug/auto-join
 *     The visitor becomes a `member` of the workspace immediately, subject
 *     to the email-domain allowlist if set. Returns `{ workspace_id }`.
 *
 *   POST /api/workspaces/by-slug/:slug/request-join
 *     Inserts a `workspace_join_requests` row in status `pending` and emits
 *     a `join_request_pending` notification to ADMIN_PLUS members. Returns
 *     `{ join_request_id, status }` — `status` is normally `"pending"`, but
 *     short-circuits to `"approved"` when the caller is already a full member
 *     (the `join_request_id` is then the existing membership row id).
 *     Idempotent: a requester with an open pending row gets that row's id
 *     back rather than a duplicate.
 *
 * The per-endpoint redaction/edge-case notes live on each `fetch*` helper.
 */

// ── Path builders ──────────────────────────────────────────────────────────

export function publicWorkspaceBySlugPath(slug: string): string {
  return `/api/workspaces/by-slug/${encodeURIComponent(slug)}/public`;
}

export function publicWorkspaceAutoJoinPath(slug: string): string {
  return `/api/workspaces/by-slug/${encodeURIComponent(slug)}/auto-join`;
}

export function publicWorkspaceRequestJoinPath(slug: string): string {
  return `/api/workspaces/by-slug/${encodeURIComponent(slug)}/request-join`;
}

export function publicWorkspacePreviewPath(slug: string): string {
  return `/api/workspaces/by-slug/${encodeURIComponent(slug)}/preview`;
}

export function publicWorkspaceChannelsPath(slug: string): string {
  return `/api/workspaces/by-slug/${encodeURIComponent(slug)}/channels`;
}

export function publicChannelContentPath(
  slug: string,
  channelName: string,
): string {
  return `/api/workspaces/by-slug/${encodeURIComponent(
    slug,
  )}/c/${encodeURIComponent(channelName)}`;
}

export function publicQuestionPath(
  slug: string,
  channelName: string,
  messageId: string,
): string {
  return `/api/workspaces/by-slug/${encodeURIComponent(
    slug,
  )}/c/${encodeURIComponent(channelName)}/m/${encodeURIComponent(messageId)}`;
}

export function publicWorkspaceContributorsPath(slug: string): string {
  return `/api/workspaces/by-slug/${encodeURIComponent(slug)}/contributors`;
}

// ── Fetch helpers ──────────────────────────────────────────────────────────

type BaseInput = {
  apiBaseUrl: string;
  fetchImpl?: FetchLike;
};

type AuthInput = BaseInput & { accessToken: string };

/**
 * Shared GET-and-validate for the unauthenticated public endpoints: normalize
 * base URL → GET → 404 ⇒ `null` → non-OK ⇒ typed error → JSON → Zod-validate.
 * Every `fetch*` helper below is a thin wrapper that supplies a path + schema.
 */
async function fetchPublicJson<T>({
  apiBaseUrl,
  fetchImpl,
  path,
  schema,
  loadError,
  invalidError,
}: BaseInput & {
  path: string;
  schema: z.ZodType<T>;
  loadError: string;
  invalidError: string;
}): Promise<T | null> {
  const fetchFn = fetchImpl ?? fetch;
  const url = `${normalizeApiBaseUrl(apiBaseUrl)}${path}`;
  const response = await fetchFn(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (response.status === 404) return null;
  if (!response.ok) {
    throw await parseErrorResponse(response, loadError);
  }
  return validateJsonBody(response, schema, invalidError);
}

/**
 * Shared POST-and-validate for the authenticated join endpoints: normalize
 * base URL → POST (Bearer auth, optional JSON body) → non-OK ⇒ typed error →
 * JSON → Zod-validate. Unlike `fetchPublicJson` there is no 404⇒null path —
 * these are writes that must succeed.
 */
async function postAuthedJson<T>({
  apiBaseUrl,
  fetchImpl,
  accessToken,
  path,
  schema,
  body,
  loadError,
  invalidError,
}: AuthInput & {
  path: string;
  schema: z.ZodType<T>;
  body?: unknown;
  loadError: string;
  invalidError: string;
}): Promise<T> {
  const fetchFn = fetchImpl ?? fetch;
  const url = `${normalizeApiBaseUrl(apiBaseUrl)}${path}`;
  const response = await fetchFn(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    throw await parseErrorResponse(response, loadError);
  }
  return validateJsonBody(response, schema, invalidError);
}

/** Parse the JSON body and Zod-validate it, throwing a typed 500 on mismatch. */
async function validateJsonBody<T>(
  response: Response,
  schema: z.ZodType<T>,
  invalidError: string,
): Promise<T> {
  const payload = await response.json().catch(() => null);
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError(invalidError, 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

export async function fetchPublicWorkspaceMetadata({
  apiBaseUrl,
  slug,
  fetchImpl,
}: BaseInput & { slug: string }): Promise<PublicWorkspaceMetadata | null> {
  return fetchPublicJson({
    apiBaseUrl,
    fetchImpl,
    path: publicWorkspaceBySlugPath(slug),
    schema: publicWorkspaceMetadataSchema,
    loadError: "Failed to look up public workspace",
    invalidError: "Invalid public-workspace response",
  });
}

/**
 * Fetch the read-only preview digest (unauthenticated). Returns `null` for a
 * non-public slug (404). The server returns `channels: []` when the owner
 * hasn't enabled `preview_then_join`.
 */
export async function fetchCommunityPreview({
  apiBaseUrl,
  slug,
  fetchImpl,
}: BaseInput & { slug: string }): Promise<CommunityPreview | null> {
  return fetchPublicJson({
    apiBaseUrl,
    fetchImpl,
    path: publicWorkspacePreviewPath(slug),
    schema: communityPreviewSchema,
    loadError: "Failed to load preview",
    invalidError: "Invalid community-preview response",
  });
}

/**
 * List the public (indexable) channels of a workspace (unauthenticated).
 * Returns `null` for a non-public slug (404) or when the owner hasn't enabled
 * anonymous viewing (the server returns an empty list in that case).
 */
export async function fetchPublicChannels({
  apiBaseUrl,
  slug,
  fetchImpl,
}: BaseInput & { slug: string }): Promise<PublicChannelsResponse | null> {
  return fetchPublicJson({
    apiBaseUrl,
    fetchImpl,
    path: publicWorkspaceChannelsPath(slug),
    schema: publicChannelsResponseSchema,
    loadError: "Failed to load channels",
    invalidError: "Invalid public-channels response",
  });
}

/**
 * Fetch a single public channel's redacted content by name (unauthenticated).
 * `null` when the workspace isn't public / anonymous viewing is off / the
 * channel isn't a public non-archived channel (404).
 */
export async function fetchPublicChannelContent({
  apiBaseUrl,
  slug,
  channelName,
  fetchImpl,
}: BaseInput & {
  slug: string;
  channelName: string;
}): Promise<PublicChannelContent | null> {
  return fetchPublicJson({
    apiBaseUrl,
    fetchImpl,
    path: publicChannelContentPath(slug, channelName),
    schema: publicChannelContentSchema,
    loadError: "Failed to load channel",
    invalidError: "Invalid public-channel response",
  });
}

/**
 * Fetch a single shared question + its answers by message id (unauthenticated).
 * `null` when the workspace isn't public / anonymous viewing is off / the
 * channel or message isn't a public non-archived top-level message (404).
 */
export async function fetchPublicQuestion({
  apiBaseUrl,
  slug,
  channelName,
  messageId,
  fetchImpl,
}: BaseInput & {
  slug: string;
  channelName: string;
  messageId: string;
}): Promise<PublicQuestionResponse | null> {
  return fetchPublicJson({
    apiBaseUrl,
    fetchImpl,
    path: publicQuestionPath(slug, channelName, messageId),
    schema: publicQuestionResponseSchema,
    loadError: "Failed to load question",
    invalidError: "Invalid public-question response",
  });
}

/**
 * Top contributors of a public workspace (unauthenticated). `null` for a
 * non-public slug (404); empty list when anonymous viewing is off or no
 * signals exist yet.
 */
export async function fetchPublicContributors({
  apiBaseUrl,
  slug,
  fetchImpl,
}: BaseInput & { slug: string }): Promise<PublicContributorsResponse | null> {
  return fetchPublicJson({
    apiBaseUrl,
    fetchImpl,
    path: publicWorkspaceContributorsPath(slug),
    schema: publicContributorsResponseSchema,
    loadError: "Failed to load contributors",
    invalidError: "Invalid contributors response",
  });
}

/**
 * A user's portable reputation across all public communities (unauthenticated;
 * public-safe aggregate). Returns zeros when the user has no signals.
 */
export async function fetchUserReputation({
  apiBaseUrl,
  userId,
  fetchImpl,
}: BaseInput & { userId: string }): Promise<UserReputation | null> {
  return fetchPublicJson({
    apiBaseUrl,
    fetchImpl,
    path: userReputationPath(userId),
    schema: userReputationSchema,
    loadError: "Failed to load reputation",
    invalidError: "Invalid reputation response",
  });
}

export async function fetchUserProfile({
  apiBaseUrl,
  userId,
  fetchImpl,
}: BaseInput & { userId: string }): Promise<UserProfile | null> {
  return fetchPublicJson({
    apiBaseUrl,
    fetchImpl,
    path: userProfilePath(userId),
    schema: userProfileSchema,
    loadError: "Failed to load profile",
    invalidError: "Invalid profile response",
  });
}

export async function fetchAutoJoinPublicWorkspace({
  apiBaseUrl,
  slug,
  accessToken,
  fetchImpl,
}: AuthInput & { slug: string }): Promise<AutoJoinResponse> {
  return postAuthedJson({
    apiBaseUrl,
    fetchImpl,
    accessToken,
    path: publicWorkspaceAutoJoinPath(slug),
    schema: autoJoinResponseSchema,
    loadError: "Failed to join workspace",
    invalidError: "Invalid auto-join response",
  });
}

export async function fetchRequestJoinPublicWorkspace({
  apiBaseUrl,
  slug,
  accessToken,
  body,
  fetchImpl,
}: AuthInput & {
  slug: string;
  body: RequestJoinBody;
}): Promise<RequestJoinResponse> {
  return postAuthedJson({
    apiBaseUrl,
    fetchImpl,
    accessToken,
    path: publicWorkspaceRequestJoinPath(slug),
    schema: requestJoinResponseSchema,
    body,
    loadError: "Failed to request to join",
    invalidError: "Invalid request-join response",
  });
}
